import { FiArrowRight } from 'react-icons/fi';

export const adverbialClausesData = {
  courseId: 'adverbial-clauses',
  courseType: 'grammar',
  title: 'Adverbial Clauses',
  icon: FiArrowRight,
  overview: {
    impact: 'Key for showing complex relationships between ideas',
    focus: 'Using clauses to express time, reason, contrast, condition',
    uses: 'Writing Task 2, complex sentences',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Types of Adverbial Clauses',
      content: `<p>Adverbial clauses modify verbs or entire sentences, showing relationships like time, reason, and contrast:</p>`,
      table: {
        headers: ['Type', 'Conjunctions', 'Example'],
        rows: [
          ['Time', 'when, while, before, after, as soon as', 'When I arrived, the meeting had started.'],
          ['Reason', 'because, since, as', 'Since you asked, I\'ll explain.'],
          ['Contrast', 'although, even though, while, whereas', 'Although it rained, we went out.'],
          ['Condition', 'if, unless, provided that', 'If you study, you will pass.'],
          ['Purpose', 'so that, in order that', 'I left early so that I wouldn\'t be late.'],
        ],
      },
    },
    {
      title: 'Position and Punctuation',
      content: `<p>Adverbial clauses can come at the beginning or end of a sentence:</p>`,
      examples: [
        { label: 'Beginning', text: 'Although technology helps, it can cause problems.', translation: 'Comma needed after the clause' },
        { label: 'End', text: 'Technology can cause problems although it helps.', translation: 'Usually no comma needed' },
      ],
      warning: 'When the adverbial clause comes first, use a comma. When it comes last, usually no comma is needed.',
    },
    {
      title: 'Useful for IELTS Task 2',
      content: `<p>Adverbial clauses help you make sophisticated arguments:</p>`,
      points: [
        'Contrast: "While technology benefits society, it also creates challenges."',
        'Condition: "If governments invest in education, economic growth will follow."',
        'Concession: "Even though some disagree, I believe this approach is effective."',
      ],
    },
  ],
  exercises: [
    {
      title: 'Complete with Adverbial Conjunctions',
      type: 'select',
      instructions: 'Choose the best conjunction.',
      questions: [
        {
          text: '___ the weather was bad, we enjoyed the trip.',
          options: ['Although', 'Because', 'If'],
          answer: 'Although',
          explanation: '"Although" shows contrast between bad weather and enjoyment.',
        },
        {
          text: 'I\'ll call you ___ I arrive.',
          options: ['as soon as', 'because', 'although'],
          answer: 'as soon as',
          explanation: '"As soon as" indicates immediate timing.',
        },
        {
          text: '___ you have questions, please ask.',
          options: ['If', 'Although', 'Because'],
          answer: 'If',
          explanation: '"If" introduces a condition.',
        },
        {
          text: 'He left early ___ he was tired.',
          options: ['because', 'although', 'if'],
          answer: 'because',
          explanation: '"Because" gives the reason.',
        },
        {
          text: '___ she was experienced, she made a mistake.',
          options: ['Even though', 'Because', 'When'],
          answer: 'Even though',
          explanation: '"Even though" shows unexpected contrast.',
        },
        {
          text: 'I study hard ___ I can pass the exam.',
          options: ['so that', 'although', 'because'],
          answer: 'so that',
          explanation: '"So that" expresses purpose.',
        },
      ],
    },
    {
      title: 'Identify the Clause Type',
      type: 'select',
      instructions: 'What type of adverbial clause is used?',
      questions: [
        {
          text: '"When the meeting ended, everyone left."',
          options: ['Time', 'Reason', 'Contrast'],
          answer: 'Time',
          explanation: '"When" indicates timing.',
        },
        {
          text: '"Since it was raining, we stayed inside."',
          options: ['Reason', 'Time', 'Condition'],
          answer: 'Reason',
          explanation: '"Since" here means "because".',
        },
        {
          text: '"Whereas cities are crowded, rural areas are peaceful."',
          options: ['Contrast', 'Time', 'Reason'],
          answer: 'Contrast',
          explanation: '"Whereas" shows a difference between two things.',
        },
        {
          text: '"Unless you hurry, you will miss the train."',
          options: ['Condition', 'Time', 'Purpose'],
          answer: 'Condition',
          explanation: '"Unless" = "if not" — introduces a condition.',
        },
      ],
    },
    {
      title: 'Add Punctuation',
      type: 'select',
      instructions: 'Does this sentence need a comma?',
      questions: [
        {
          text: 'Although it rained we went outside.',
          options: ['Yes, after "rained"', 'No comma needed', 'Yes, before "although"'],
          answer: 'Yes, after "rained"',
          explanation: 'When the adverbial clause comes first, add a comma.',
        },
        {
          text: 'We went outside although it rained.',
          options: ['No comma needed', 'Yes, before "although"', 'Yes, after "outside"'],
          answer: 'No comma needed',
          explanation: 'When the adverbial clause comes last, usually no comma.',
        },
        {
          text: 'If you study hard you will succeed.',
          options: ['Yes, after "hard"', 'No comma needed', 'Yes, before "if"'],
          answer: 'Yes, after "hard"',
          explanation: 'Comma needed when "if" clause comes first.',
        },
      ],
    },
    {
      title: 'Write Adverbial Clause Sentences',
      type: 'writing',
      instructions: 'Write sentences using the specified clause types.',
      questions: [
        {
          text: 'Write a sentence with a CONTRAST clause about technology.',
          example: 'Although technology has many benefits, it can also create privacy concerns.',
          tip: 'Use "although," "even though," or "while."',
        },
        {
          text: 'Write a sentence with a CONDITION clause about education.',
          example: 'If governments invest more in education, economic growth will follow.',
          tip: 'Use "if," "unless," or "provided that."',
        },
        {
          text: 'Write a sentence with a PURPOSE clause.',
          example: 'Many people exercise regularly so that they can maintain good health.',
          tip: 'Use "so that" or "in order that."',
        },
      ],
    },
  ],
  tips: [
    'Use contrast clauses to acknowledge opposing views',
    'Vary your position—start some sentences with adverbial clauses',
    'Master conditional sentences for hypothetical arguments',
  ],
  dailyPractice: [
    'Write sentences with contrast adverbial clauses',
    'Practice different clause positions',
    'Use adverbial clauses in essay paragraphs',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Adverbial Clauses: Clauses that modify verbs/sentences, showing time, reason, contrast, condition, and purpose. Important for complex IELTS writing.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...adverbialClausesData,
      title: adverbialClausesData.title + ' (Foundations)',
      overview: {
        ...adverbialClausesData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: adverbialClausesData.sections.slice(0, Math.max(2, Math.ceil(adverbialClausesData.sections.length / 2))),
      exercises: adverbialClausesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...adverbialClausesData,
      title: adverbialClausesData.title + ' (Advanced)',
      overview: {
        ...adverbialClausesData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: adverbialClausesData.sections,
      exercises: adverbialClausesData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(adverbialClausesData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return adverbialClausesData;
}
